import { exVat } from './revenue'
import { toCents } from '../format/currency'
import { giftStockBookings } from './giftTiers'
import type { GiftTier } from '../types/site'
import type { CostModel, FeeKey, MarginBreakdown, OrderKind, PaymentFee, ShipKind } from '../types/margin'

/**
 * "Wat er overblijft": omzet zonder btw min inkoop, cadeaus, betaal-, verzend- en verpakkingskosten
 * en een retourvoorziening. Puur, in centen; getest in margin.test.ts. De server rekent hetzelfde
 * (LARAVEL.md §9.8). Bijdrage aan vaste lasten, geen nettowinst: huur, software en eigen tijd tellen niet.
 */

const ROUND1 = (n: number) => Math.round(n * 10) / 10

const FEE_OF: Record<string, FeeKey | null> = {
  ideal: 'ideal',
  wero: 'ideal',
  bancontact: 'bancontact',
  card: 'card',
  creditcard: 'card',
  klarna: 'klarna',
  incasso: 'incasso',
  directdebit: 'incasso',
  pin: 'pin',
  contant: null,
  cadeaubon: null,
  tegoed: null,
}

/** Kosten van één betaling: vast bedrag plus percentage. Niets betaald = geen kosten. */
export const feeCents = (fee: PaymentFee | undefined, amountCents: number): number =>
  !fee || amountCents <= 0 ? 0 : Math.round(fee.fixedCents + (amountCents * fee.percent) / 100)

/** Betaalkosten per manier; onbekend, contant, cadeaubon of tegoed = € 0. */
export const paymentCostCents = (model: CostModel, method: string, amountCents: number): number => {
  const key = FEE_OF[method]
  return key ? feeCents(model.payment[key], amountCents) : 0
}

export const packagingFor = (model: CostModel, ship: ShipKind | null): number =>
  ship === null ? 0 : ship === 'brievenbus-nl' ? model.packaging.brievenbus : model.packaging.pakket

/** Marge van één product: verkoop zonder btw min de (gemiddelde) inkoop. */
export function productMargin(grossCents: number, vatRate: number, costCents: number | null | undefined) {
  const netCents = exVat(grossCents, vatRate)
  if (costCents === null || costCents === undefined) return { netCents, marginCents: null, percent: null }
  const marginCents = netCents - costCents
  return { netCents, marginCents, percent: netCents > 0 ? ROUND1((marginCents / netCents) * 100) : null }
}

export type MarginLine = {
  name: string
  qty: number
  /** De hele regel, incl. btw, zoals verkocht. */
  grossCents: number
  vatRate: number
  /** Inkoop per stuk zonder btw; `null` = onbekend. */
  costCents: number | null
}

export type OrderCostInput = {
  kind: OrderKind
  lines: MarginLine[]
  paymentMethod: string
  /** Wat via de betaalprovider of pin binnenkwam (zonder het deel van een cadeaubon). */
  paidCents: number
  shipping: ShipKind | null
  /** Verzendkosten die de klant betaalde, incl. btw. */
  shippingChargedCents?: number
  shippingVatRate?: number
  giftCostCents?: number
  /** Al terugbetaald, zonder btw. */
  refundedNetCents?: number
  /** Inkoop van producten die ongeopend terugkwamen en weer verkoopbaar zijn (retour op voorraad). */
  returnedCostCents?: number
}

/** Wat er overblijft van één bestelling of kassabon. */
export function orderContribution(i: OrderCostInput, model: CostModel): MarginBreakdown {
  const lineNet = i.lines.reduce((s, l) => s + exVat(l.grossCents, l.vatRate), 0)
  const shipNet = i.shippingChargedCents ? exVat(i.shippingChargedCents, i.shippingVatRate ?? 21) : 0
  const revenueNetCents = lineNet + shipNet - (i.refundedNetCents ?? 0)
  const purchaseCents = i.lines.reduce((s, l) => s + (l.costCents ?? 0) * l.qty, 0) - (i.returnedCostCents ?? 0)
  const giftCents = i.giftCostCents ?? 0
  const paymentCents = paymentCostCents(model, i.paymentMethod, i.paidCents)
  const shippingCents = i.shipping ? model.shipping[i.shipping] : 0
  const packagingCents = packagingFor(model, i.shipping)
  const returnsCents = i.kind === 'salon' ? 0 : Math.round((lineNet * model.returnsPercent) / 100)
  const leftCents = revenueNetCents - purchaseCents - giftCents - paymentCents - shippingCents - packagingCents - returnsCents
  return {
    revenueNetCents,
    purchaseCents,
    giftCents,
    paymentCents,
    shippingCents,
    packagingCents,
    returnsCents,
    leftCents,
    leftPercent: revenueNetCents > 0 ? ROUND1((leftCents / revenueNetCents) * 100) : null,
    missingCost: i.lines.filter((l) => l.costCents === null).map((l) => l.name),
  }
}

/**
 * Inkoopwaarde van de staffelcadeaus bij een mand of order: dezelfde treden en voorraadartikelen
 * die de server bij betalen afboekt (`giftStockBookings`). Een keuze die de order niet bewaart,
 * valt terug op de eerste optie (zoals de server). Artikel zonder inkoopprijs telt als 0 en staat in `unknown`.
 */
export function staffelGiftCost(
  tiers: readonly GiftTier[],
  freeShippingFrom: number,
  subtotalEuro: number,
  itemCosts: Readonly<Record<string, number>>,
): { cents: number; unknown: number } {
  const bookings = giftStockBookings(tiers, freeShippingFrom, subtotalEuro)
  return bookings.reduce(
    (t, b) => (itemCosts[b.itemId] === undefined ? { ...t, unknown: t.unknown + 1 } : { ...t, cents: t.cents + (itemCosts[b.itemId] ?? 0) }),
    { cents: 0, unknown: 0 },
  )
}

/** Een product zoals de catalogus het kent (prijs in euro's, incl. btw). */
type PricedProduct = { id: string; name: string; price: number; vatRate?: number; kind?: 'skincare' | 'giftcard' | 'kuur' }

/** Catalogusproducten → margeregels, met de gemiddelde inkoop uit `itemCosts` (onbekend = `null`). */
export const marginLines = (products: readonly PricedProduct[], itemCosts: Record<string, number>, vatRateOf: (p: PricedProduct) => number): MarginLine[] =>
  products.map((p) => ({ name: p.name, qty: 1, grossCents: toCents(p.price), vatRate: vatRateOf(p), costCents: itemCosts[p.id] ?? null }))

/**
 * Brutomarge van een mand of protocol in % van de omzet zonder btw. `null` als één product geen
 * inkoopprijs heeft: een half getal zou te mooi zijn.
 */
export function basketMarginPercent(lines: readonly MarginLine[]): number | null {
  if (lines.length === 0 || lines.some((l) => l.costCents === null)) return null
  const net = lines.reduce((s, l) => s + exVat(l.grossCents, l.vatRate), 0)
  const cost = lines.reduce((s, l) => s + (l.costCents ?? 0) * l.qty, 0)
  return net > 0 ? ROUND1(((net - cost) / net) * 100) : null
}

/** Vaste kosten van één bijvuldoos: incasso, pakket NL, verpakking (zonder percentages). */
export const boxFixedCents = (model: CostModel) =>
  model.payment.incasso.fixedCents + model.shipping['pakket-nl'] + model.packaging.pakket

/**
 * Wat er overblijft van een bijvuldoos van `boxGrossCents` (incl. btw) bij een brutomarge van
 * `marginPercent` (% van de omzet zonder btw). Geen staffelcadeau; wel het abonnementscadeau als dat erin zit.
 */
export function boxContribution(boxGrossCents: number, marginPercent: number, model: CostModel, opts: { vatRate?: number; withGift?: boolean } = {}) {
  const net = exVat(boxGrossCents, opts.vatRate ?? 21)
  const gross = Math.round((net * marginPercent) / 100)
  const costs =
    paymentCostCents(model, 'incasso', boxGrossCents) +
    model.shipping['pakket-nl'] +
    model.packaging.pakket +
    Math.round((net * model.returnsPercent) / 100) +
    (opts.withGift ? model.subscriptionGiftCents : 0)
  return gross - costs
}

/**
 * Het kleinste bijvulminimum (hele euro's, incl. btw) waarbij een doos minstens `targetCents`
 * overlaat. `null` als de marge de kosten nooit dekt.
 */
export function minBoxFor(targetCents: number, marginPercent: number, model: CostModel, vatRate = 21): number | null {
  const share = (marginPercent - model.returnsPercent - model.payment.incasso.percent * (1 + vatRate / 100)) / 100
  if (share <= 0) return null
  const net = (targetCents + boxFixedCents(model)) / share
  return Math.ceil((net * (1 + vatRate / 100)) / 100)
}

/**
 * Gemiddelde inkoopprijs na een levering. Een gratis regel (€ 0 of geen prijs, bijv. samples met
 * 100% korting) verandert het gemiddelde niet; zonder bekende voorraad of prijs telt de nieuwe prijs.
 */
export function averageCost(stockQty: number, currentCents: number | undefined, addQty: number, addCents: number | undefined): number | undefined {
  if (addCents === undefined || addCents <= 0 || addQty <= 0) return currentCents
  if (currentCents === undefined || stockQty <= 0) return addCents
  return Math.round((stockQty * currentCents + addQty * addCents) / (stockQty + addQty))
}

/**
 * Soort bestelling als de server hem niet meegaf: salon, bijvuldoos (kanaal `abonnement`),
 * protocol (er zit een set in) of los.
 */
export const orderKindOf = (channel: string, lines: readonly { bundleRoutineId?: string }[]): OrderKind =>
  channel === 'salon' ? 'salon' : channel === 'abonnement' ? 'bijvullen' : lines.some((l) => l.bundleRoutineId) ? 'protocol' : 'los'

export const orderKindLabel: Record<OrderKind, string> = {
  salon: 'Salon (kassa)',
  protocol: 'Protocollen',
  bijvullen: 'Bijvuldozen',
  los: 'Losse bestellingen',
}
